import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, ChevronRight, Handshake, MapPin, Megaphone, Mic, Radio, Search, Sparkles, Target, TrendingUp, Users } from 'lucide-react';

import { artistsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const CITY_PRESETS = ['Atlanta', 'Los Angeles', 'London', 'Lagos', 'Nashville', 'New York'];

export default function LocalScenePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCity = searchParams.get('city') || 'Atlanta';

  const [cityInput, setCityInput] = useState(initialCity);
  const [city, setCity] = useState(initialCity);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchScene = async () => {
      setLoading(true);
      try {
        const res = await artistsAPI.localScene(city);
        setData(res.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchScene();
  }, [city]);

  const handleSearch = (event) => {
    event.preventDefault();
    if (!cityInput.trim()) return;
    const nextCity = cityInput.trim();
    setCity(nextCity);
    navigate(`/local-scene?city=${encodeURIComponent(nextCity)}`);
  };

  const summary = data?.summary || {};

  return (
    <div className="min-h-screen bg-zinc-50 pb-20" data-testid="local-scene-page">
      <section className="relative overflow-hidden bg-zinc-950 px-4 py-16 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,170,85,0.18),transparent_60%)]" />
        <div className="relative mx-auto max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-emerald-300">
              <Radio size={12} /> Local Scene Domination
            </span>
            <h1 className="font-display text-4xl font-black leading-tight text-white sm:text-5xl lg:text-6xl" data-testid="local-scene-title">
              Own your city before the whole world catches on.
            </h1>
            <p className="mt-4 max-w-2xl text-sm text-zinc-300 sm:text-base">
              Track rising local artists, live events, active funding projects, and the exact promo plays that can make a scene feel unstoppable.
            </p>

            <form onSubmit={handleSearch} className="mt-8 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <Input value={cityInput} onChange={(event) => setCityInput(event.target.value)} placeholder="Search a city or region" className="h-12 border-white/10 bg-white/5 pl-11 text-white placeholder:text-zinc-500" data-testid="local-scene-search-input" />
              </div>
              <Button type="submit" className="h-12 rounded-full bg-emerald-400 px-6 font-bold text-black hover:bg-emerald-300" data-testid="local-scene-search-button">
                <Sparkles size={16} className="mr-2" /> Build the board
              </Button>
            </form>

            <div className="mt-5 flex flex-wrap gap-2">
              {CITY_PRESETS.map((preset) => (
                <button key={preset} onClick={() => { setCityInput(preset); setCity(preset); navigate(`/local-scene?city=${encodeURIComponent(preset)}`); }} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${city === preset ? 'border-emerald-300 bg-emerald-300/10 text-emerald-300' : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:text-white'}`} data-testid={`local-scene-city-${preset.toLowerCase().replace(/\s+/g, '-')}`}>
                  {preset}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { label: 'Artists', value: summary.total_artists || 0 },
              { label: 'Projects', value: summary.total_projects || 0 },
              { label: 'Events', value: summary.total_events || 0 },
              { label: 'Raised', value: formatCurrency(summary.total_raised || 0) },
              { label: 'Tickets Sold', value: summary.total_tickets_sold || 0 },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm" data-testid={`local-scene-stat-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-xs uppercase tracking-[0.16em] text-zinc-500">{item.label}</p>
                <p className="mt-2 font-display text-2xl font-black text-white">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10">
        {loading ? (
          <div className="grid gap-4 md:grid-cols-3">
            {[...Array(6)].map((_, index) => <div key={index} className="h-44 animate-pulse rounded-3xl bg-zinc-200" />)}
          </div>
        ) : !data?.artists?.length ? (
          <div className="rounded-3xl border border-zinc-200 bg-white p-10 text-center" data-testid="local-scene-empty-state">
            <MapPin size={40} className="mx-auto mb-4 text-zinc-300" />
            <h2 className="font-display text-2xl font-bold text-zinc-900">No scene data yet for {city}</h2>
            <p className="mt-2 text-sm text-zinc-500">Try another city, or seed more creators and events into this region.</p>
          </div>
        ) : (
          <div className="space-y-10">
            <section data-testid="local-scene-leaderboard-section">
              <div className="mb-6 flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Leaderboard</p>
                  <h2 className="font-display text-3xl font-bold text-zinc-900">Who has the most local momentum in {data.location}?</h2>
                </div>
                <div className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-semibold text-zinc-500">
                  Ranked by followers, active projects, revenue, events, and tickets sold
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                {data.artists.slice(0, 6).map((artist, index) => (
                  <motion.div key={artist.id} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06 }} className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid={`local-scene-artist-${artist.id}`}>
                    <div className="flex items-start gap-4">
                      {artist.avatar ? <img src={artist.avatar} alt={artist.name} className="h-16 w-16 rounded-2xl object-cover" /> : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-200 text-lg font-black text-zinc-500">{artist.name?.[0]}</div>}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">#{index + 1} in {data.location}</p>
                            <h3 className="truncate font-display text-xl font-bold text-zinc-900">{artist.name}</h3>
                          </div>
                          <div className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-black text-emerald-600">
                            {artist.local_momentum_score}
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-zinc-500">{artist.genre} • {artist.location || data.location}</p>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-2xl bg-zinc-50 p-3"><p className="text-xs text-zinc-400">Followers</p><p className="font-bold text-zinc-900">{artist.stats.followers}</p></div>
                      <div className="rounded-2xl bg-zinc-50 p-3"><p className="text-xs text-zinc-400">Raised</p><p className="font-bold text-zinc-900">{formatCurrency(artist.stats.raised)}</p></div>
                      <div className="rounded-2xl bg-zinc-50 p-3"><p className="text-xs text-zinc-400">Projects</p><p className="font-bold text-zinc-900">{artist.stats.projects}</p></div>
                      <div className="rounded-2xl bg-zinc-50 p-3"><p className="text-xs text-zinc-400">Events</p><p className="font-bold text-zinc-900">{artist.stats.events}</p></div>
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-zinc-600">{artist.outreach_hook}</p>
                    <Link to={`/artist/${artist.id}`} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700" data-testid={`local-scene-artist-link-${artist.id}`}>
                      Open artist profile <ChevronRight size={14} />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr]">
              <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="local-scene-projects-section">
                <div className="mb-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">Funding now</p>
                    <h2 className="font-display text-2xl font-bold text-zinc-900">Local projects with breakout potential</h2>
                  </div>
                  <Target size={18} className="text-emerald-500" />
                </div>
                <div className="space-y-3">
                  {data.projects.slice(0, 5).map((project) => (
                    <Link key={project.id} to={`/projects/${project.id}`} className="flex items-center justify-between gap-4 rounded-2xl border border-zinc-200 p-4 transition-colors hover:border-emerald-300" data-testid={`local-scene-project-${project.id}`}>
                      <div>
                        <h3 className="font-semibold text-zinc-900">{project.title}</h3>
                        <p className="mt-1 text-xs text-zinc-500">{project.creator_name} • {project.genre}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-black text-emerald-600">{project.discovery_score}</p>
                        <p className="text-xs text-zinc-400">{project.funded_pct}% funded</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="local-scene-events-section">
                <div className="mb-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">Rooms that matter</p>
                    <h2 className="font-display text-2xl font-bold text-zinc-900">Upcoming local event pressure points</h2>
                  </div>
                  <Calendar size={18} className="text-amber-500" />
                </div>
                <div className="space-y-3">
                  {data.events.slice(0, 5).map((event) => (
                    <Link key={event.id} to={`/events/${event.id}`} className="block rounded-2xl border border-zinc-200 p-4 transition-colors hover:border-amber-300" data-testid={`local-scene-event-${event.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-zinc-900">{event.title}</h3>
                          <p className="mt-1 text-xs text-zinc-500">{event.venue || event.location || data.location}</p>
                        </div>
                        <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-600">{event.total_tickets_sold} sold</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>

            <section className="grid gap-6 lg:grid-cols-[1fr,1fr]">
              <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="local-scene-playbook-section">
                <div className="mb-5 flex items-center gap-3">
                  <Megaphone size={18} className="text-violet-500" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">Promo playbooks</p>
                    <h2 className="font-display text-2xl font-bold text-zinc-900">What to do next in {data.location}</h2>
                  </div>
                </div>
                <div className="space-y-4">
                  {data.promoter_playbook?.map((play) => (
                    <div key={play.title} className="rounded-2xl bg-zinc-50 p-4" data-testid={`local-scene-play-${play.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <h3 className="font-semibold text-zinc-900">{play.title}</h3>
                        <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${play.priority === 'high' ? 'bg-red-100 text-red-600' : 'bg-zinc-200 text-zinc-600'}`}>{play.priority}</span>
                      </div>
                      <p className="text-sm text-zinc-600">{play.summary}</p>
                      <ul className="mt-3 space-y-2 text-sm text-zinc-600">
                        {play.steps?.map((step) => <li key={step} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500" /> <span>{step}</span></li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="local-scene-outreach-section">
                  <div className="mb-5 flex items-center gap-3">
                    <Mic size={18} className="text-emerald-500" />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">Outreach copy angles</p>
                      <h2 className="font-display text-2xl font-bold text-zinc-900">Who should you push hardest right now?</h2>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {data.outreach_suggestions?.map((item) => (
                      <div key={item.artist_id} className="rounded-2xl border border-zinc-200 p-4" data-testid={`local-scene-outreach-${item.artist_id}`}>
                        <p className="font-semibold text-zinc-900">{item.artist_name}</p>
                        <p className="mt-2 text-sm text-zinc-600"><span className="font-semibold text-zinc-800">Angle:</span> {item.angle}</p>
                        <p className="mt-2 text-sm text-zinc-600"><span className="font-semibold text-zinc-800">Hook:</span> {item.hook}</p>
                        <p className="mt-2 text-sm text-zinc-600"><span className="font-semibold text-zinc-800">CTA:</span> {item.cta}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="local-scene-hotspots-section">
                  <div className="mb-4 flex items-center gap-3">
                    <Handshake size={18} className="text-amber-500" />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">Where to stack attention</p>
                      <h2 className="font-display text-2xl font-bold text-zinc-900">Hotspots and partnership bets</h2>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.hotspots?.length ? data.hotspots.map((spot) => (
                      <span key={spot.name} className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-semibold text-zinc-600" data-testid={`local-scene-hotspot-${spot.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                        {spot.name} • {spot.count}
                      </span>
                    )) : <span className="text-sm text-zinc-500">No recurring venue pattern yet.</span>}
                  </div>

                  <div className="mt-5 space-y-3">
                    {data.partnership_opportunities?.map((item) => (
                      <div key={item.artist_id} className="rounded-2xl bg-zinc-50 p-4" data-testid={`local-scene-partnership-${item.artist_id}`}>
                        <p className="font-semibold text-zinc-900">{item.artist_name}</p>
                        <p className="mt-1 text-sm text-zinc-600">{item.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}